<template>
  <q-header elevated class="bg-dark text-white">
    <q-toolbar class="q-px-md toolbar-compact row no-wrap items-center">
      
      <!-- Titre aligné à gauche (alimenté dynamiquement par chaque page) -->
      <div class="col-3 text-subtitle1 text-weight-bold ellipsis text-left">
        {{ headerTitle }}
      </div>

      <!-- Menu principal centré -->
      <div class="col-6 row justify-center">
        <q-tabs
          dense
          active-color="primary"
          indicator-color="primary"
          class="text-grey-4"
        >
          <q-route-tab
            to="/production"
            icon="precision_manufacturing"
            :label="t('production.description')"
            exact
          />
          <q-route-tab
            to="/receipe"
            icon="auto_awesome_motion"
            :label="t('receipe.description')"
            exact
          />
          <q-route-tab
            to="/loop"
            icon="loop"
            :label="t('loop.description')"
            exact
          />
          <q-route-tab
            to="/program"
            icon="account_tree"
            :label="t('program.description')"
            exact
          />
          <q-route-tab
            to="/configuration"
            icon="settings"
            :label="t('configuration.description')"
            exact
          />
          <q-btn
            flat
            no-caps
            icon="view_in_ar"
            label="3D"
            @click="openSimulationWindow"
          >
            <q-tooltip>{{ t('visualization.openWindow') }}</q-tooltip>
          </q-btn>
        </q-tabs>
        <q-btn
          unelevated
          no-caps
          icon="science"
          :label="dryRun ? t('dryRun.activeButton') : t('dryRun.button')"
          :color="dryRun ? 'warning' : 'grey-8'"
          :text-color="dryRun ? 'black' : 'white'"
          :loading="dryRunLoading"
          :disable="dryRunLoading || !backendOnline"
          @click="toggleDryRun"
          class="dry-run-btn"
        />
        <q-btn
          unelevated
          no-caps
          icon="terminal"
          :label="t('consolePopup.button')"
          color="grey-8"
          text-color="white"
          class="console-btn"
          @click="consoleDialog = true"
        >
          <q-badge
            v-if="messages.length"
            floating
            rounded
            color="primary"
            :label="messages.length > 99 ? '99+' : messages.length"
          />
        </q-btn>
      </div>

   

      <!-- Espaceur droit pour équilibrer la grille -->
      <div class="col-3 row justify-end items-center q-gutter-sm">
        <q-btn flat round icon="logout" to="/logout_redirect" :aria-label="t('authentication.logout')">
          <q-tooltip>{{ t('authentication.logout') }}</q-tooltip>
        </q-btn>
        <q-btn
          flat
          dense
          round
          size="sm"
          @click="toggleLanguage"
          class="language-btn"
        >
          {{ currentLanguage }}
        </q-btn>
        <div class="header-logo-shell">
          <img src="/ONX-Logo_VF_RGB.png" alt="ONX" class="header-logo">
        </div>
      </div>

    </q-toolbar>

    <q-dialog v-model="consoleDialog">
      <q-card class="console-dialog-card">
        <q-card-section class="console-dialog-header">
          <div class="console-dialog-title">
            <q-icon name="terminal" />
            <div>
              <strong>{{ t('consolePopup.title') }}</strong>
              <span :class="consoleConnected ? 'text-positive' : 'text-negative'">
                {{ consoleConnected ? t('consolePopup.connected') : t('consolePopup.disconnected') }}
              </span>
            </div>
          </div>
          <div class="row items-center q-gutter-xs">
            <q-btn flat round dense icon="delete_sweep" :aria-label="t('consolePopup.clear')" @click="clearMessages" />
            <q-btn v-close-popup flat round dense icon="close" :aria-label="t('consolePopup.close')" />
          </div>
        </q-card-section>

        <q-separator dark />

        <q-card-section class="console-dialog-body">
          <div v-if="!messages.length" class="console-empty">
            <q-icon name="terminal" />
            <span>{{ t('consolePopup.empty') }}</span>
          </div>
          <div v-else class="console-output">
            <div v-for="(message, index) in messages" :key="index" class="console-output-line">
              {{ formatConsoleMessage(message) }}
            </div>
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-header>
</template>

<script setup lang="ts">
// État partagé pour le titre
const headerTitle = useState<string>('headerTitle', () => '')

// i18n configuration
const { locale, setLocale } = useI18n()

const currentLanguage = computed(() => {
  return locale.value === 'fr' ? 'FR' : 'EN'
})
const { t } = useI18n();
const toggleLanguage = () => {
  const newLocale = locale.value === 'fr' ? 'en-US' : 'fr'
  setLocale(newLocale)
}

// Dry Run state
const dryRun = useState<boolean>('dryRun', () => false)
const simulationMode = useState<boolean>('simulationMode', () => false)
const openSimulationWindow = () => {
  window.open('/visualisation?standalone=1', 'robot-simulation', 'popup=yes,width=1400,height=900,resizable=yes')
}
const backendOnline = useState<boolean>('backendOnline', () => true)
const dryRunLoading = ref(false)
const { notify } = useQuasar()
const config = useRuntimeConfig()
const consoleDialog = ref(false)
const consoleUrl = useRobotWebSocketUrl()
const {
  messages,
  connect: connectConsole,
  clearMessages,
  isConnected: consoleConnected,
  lastMessageAt: consoleLastMessageAt
} = useWebSocket(consoleUrl)

const formatConsoleMessage = (message: Record<string, any>) => {
  if (config.public["DEBUG"] == "false") {
    return message.console ?? message.raw ?? ''
  }
  return JSON.stringify(message)
}
const toggleDryRun = async () => {
  const requestedState = !dryRun.value
  dryRunLoading.value = true
  try {
    const response = await robot(requestedState ? 'dryRun_on' : 'dryRun_off')
    dryRun.value = response?.dry_run === true
    backendOnline.value = true
  } catch (error) {
    // A fresh backend event is also a heartbeat while the HTTP proxy is slow.
    backendOnline.value = consoleConnected.value && consoleLastMessageAt.value > 0
      && Date.now() - consoleLastMessageAt.value < 10000
    notify({
      type: 'negative',
      message: t('dryRun.error'),
      position: 'top'
    })
  } finally {
    dryRunLoading.value = false
  }
}

const refreshRuntimeStatus = async () => {
  if (runtimeStatusPending) return
  runtimeStatusPending = true
  try {
    const response = await apiFetch('/api/runtime-status', {
      method: 'GET',
      timeout: 3000,
      retry: 0
    })
    dryRun.value = response?.dry_run === true
    simulationMode.value = response?.simulation === true
    backendOnline.value = response?.online === true
  } catch (error) {
    backendOnline.value = false
  } finally {
    runtimeStatusPending = false
  }
}

let runtimeStatusTimer: ReturnType<typeof setInterval> | undefined
let runtimeStatusPending = false

onMounted(async () => {
  connectConsole()
  await refreshRuntimeStatus()
  runtimeStatusTimer = setInterval(refreshRuntimeStatus, 5000)
})

onUnmounted(() => {
  if (runtimeStatusTimer) clearInterval(runtimeStatusTimer)
})
</script>

<style scoped>
@import "~/assets/styles/onx.css";
.toolbar-compact {
  min-height: 44px;
}

:deep(.q-tab) {
  min-height: 44px;
  padding: 0 12px;
}

.q-tab--active {
  font-weight: bold;
}

.dry-run-btn {
  min-height: 36px;
  margin-left: 10px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 14px;
}

.console-btn {
  min-height: 36px;
  margin-left: 8px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 14px;
}

.console-dialog-card {
  width: min(820px, 92vw);
  max-width: 820px;
  overflow: hidden;
  color: #eef3f6;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 14px;
  background: #2e3236;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.42);
}

.console-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 68px;
  padding: 14px 18px;
}

.console-dialog-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.console-dialog-title > .q-icon {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border-radius: 9px;
  color: #64b5f6;
  background: rgba(33, 150, 243, 0.16);
  font-size: 22px;
}

.console-dialog-title > div {
  display: flex;
  flex-direction: column;
}

.console-dialog-title strong {
  font-size: 17px;
}

.console-dialog-title span {
  margin-top: 2px;
  font-size: 12px;
}

.console-dialog-body {
  height: min(62vh, 560px);
  padding: 0;
  background: #1d2023;
}

.console-output {
  height: 100%;
  overflow-y: auto;
  padding: 14px 16px;
  font: 13px/1.55 Consolas, "Courier New", monospace;
}

.console-output-line {
  padding: 5px 0;
  overflow-wrap: anywhere;
  color: #d6dde3;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.console-empty {
  display: grid;
  height: 100%;
  place-content: center;
  justify-items: center;
  gap: 10px;
  color: #8d969e;
}

.console-empty .q-icon {
  font-size: 34px;
}

.dry-run-btn.bg-warning {
  font-weight: bold;
}

.header-logo-shell {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 3px 9px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
}

.header-logo {
  display: block;
  width: auto;
  height: 29px;
  object-fit: contain;
}

@media (max-width: 850px) {
  .header-logo-shell {
    height: 32px;
    padding: 3px 6px;
  }

  .header-logo {
    height: 24px;
  }
}

@media (max-width: 650px) {
  .header-logo-shell {
    display: none;
  }
}

.language-btn {
  min-width: 32px;
  height: 32px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  font-weight: bold;
  font-size: 12px;
}

.language-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}
</style>
